import { useEffect, useState, useMemo } from "react";
import DraggableList from "components/Common/DraggableList";
import { Row, Col, Button, Card, CardBody, Input, Spinner } from "reactstrap";
import { useDynamicForm } from "hooks/useDynamicForm";
import { useVehicleSearchQuery } from "hooks/stockHook";
import { useMediaQuery } from "react-responsive";

const DEFAULT_SPECS = [
  { id: "1", key: "Vehicle Name", value: "", type: "text" },
  { id: "2", key: "Chassis No", value: "", type: "text" },
  { id: "3", key: "Engine No", value: "", type: "text" },
  { id: "4", key: "Model", value: "", type: "text" },
  { id: "5", key: "Color", value: "", type: "text" },
  { id: "6", key: "CC", value: "", type: "text" },
  { id: "7", key: "Fuel Type", value: "", type: "text" },
  { id: "8", key: "Descriptions", value: "", type: "textarea" },
];

const VehicleSpecification = ({
  onVehicleSpecsChange,
  initialVehicleSpecs = [],
  customSearchAPI,
  selectedVehicle,
  setSelectedVehicle,
  existVehicleLoading,
  existVehicleData,
}) => {
  const isMobile = useMediaQuery({ maxWidth: 767 })

  const [showVehicleSuggestions, setShowVehicleSuggestions] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")

  const {
    data: searchResults,
    isLoading: searchLoading,
    error: searchError,
  } = useVehicleSearchQuery(searchTerm, {
    limit: 3,
    enabled: searchTerm?.length >= 2,
  })

  const {
    items: vehicleSpecs,
    addItem: addVehicleSpec,
    removeItem: removeVehicleSpec,
    updateItem: updateVehicleSpec,
    handleDragEnd: handleSpecsDragEnd,
    resetItems: resetVehicleSpecs,
  } = useDynamicForm(
    initialVehicleSpecs.length > 0 ? initialVehicleSpecs : DEFAULT_SPECS
  )

  const handleVehicleSelect = vehicle => {
    setSelectedVehicle(vehicle)
    const updatedSpecs = vehicleSpecs?.map(spec => {
      vehicle.full_name = `${vehicle?.brand?.name ?? ""} ${
        vehicle?.model?.name ?? ""
      } ${vehicle?.package?.name ?? ""} ${
        vehicle?.engine_type?.name ?? ""
      }`.trim()

      switch (spec.key) {
        case "Vehicle Name":
          return { ...spec, value: vehicle.full_name ?? "" }
        case "Chassis No":
          return {
            ...spec,
            value: vehicle?.full_chassis ?? vehicle?.chassis_code?.name ?? "",
          }
        case "Engine No":
          return {
            ...spec,
            value: vehicle?.full_engine ?? vehicle?.engine_code?.name ?? "",
          }
        case "Model":
          return { ...spec, value: vehicle?.year?.name ?? "" }
        case "Color":
          return { ...spec, value: vehicle?.exterior_color?.name ?? "" }
        case "CC":
          return { ...spec, value: vehicle?.engine_c_c?.name ?? "" }
        case "Fuel Type":
          return { ...spec, value: vehicle?.fuel_type?.name ?? "" }
        case "Descriptions":
          return { ...spec, value: vehicle?.short_descriptions ?? "" }
        default:
          return spec
      }
    })
    resetVehicleSpecs(updatedSpecs)
    setShowVehicleSuggestions(false)
    setSearchTerm("")

    if (onVehicleSpecsChange) {
      onVehicleSpecsChange(updatedSpecs)
    }
  }

  const handleManualSearch = () => {
    if (searchTerm?.length >= 2) {
      if (customSearchAPI) {
        customSearchAPI(searchTerm)
      }
    }
  }

  const handleCloseSuggestions = () => {
    setShowVehicleSuggestions(false)
    setSearchTerm("")
  }

  const handleUpdateVehicleSpec = (id, field, value) => {
    updateVehicleSpec(id, field, value)
    if (onVehicleSpecsChange) {
      const updatedSpecs = vehicleSpecs?.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
      onVehicleSpecsChange(updatedSpecs)
    }
  }

  const handleAddVehicleSpec = newSpec => {
    addVehicleSpec(newSpec)
    if (onVehicleSpecsChange) {
      onVehicleSpecsChange([...vehicleSpecs, newSpec])
    }
  }

  const handleRemoveVehicleSpec = id => {
    removeVehicleSpec(id)
    if (onVehicleSpecsChange) {
      onVehicleSpecsChange(vehicleSpecs.filter(item => item.id !== id))
    }
  }

  useEffect(() => {
    if (!initialVehicleSpecs?.length) return
    resetVehicleSpecs(initialVehicleSpecs)
  }, [initialVehicleSpecs, resetVehicleSpecs])

  useEffect(() => {
    if (existVehicleData) {
      handleVehicleSelect(existVehicleData)
    }
  }, [existVehicleData])

  const renderVehicleSpec = (spec, index) => (
    <Row className="g-2 mb-2">
      <Col xs={12} sm={5} md={5} lg={5} xl={5}>
        <Input
          type="text"
          value={spec.key}
          onChange={e =>
            handleUpdateVehicleSpec(spec.id, "key", e.target.value)
          }
          placeholder="Specification name"
          className="form-control-sm"
          size={isMobile ? "sm" : undefined}
        />
      </Col>
      <Col xs={12} sm={7} md={7} lg={7} xl={7}>
        <Input
          type={spec?.type === "textarea" ? "textarea" : "text"}
          value={spec.value}
          onChange={e =>
            handleUpdateVehicleSpec(spec.id, "value", e.target.value)
          }
          style={{"field-sizing": 'content'}}
          placeholder="Enter value"
          className="form-control-sm"
          size={isMobile ? "sm" : undefined}
        />
      </Col>
    </Row>
  )

  return (
    <Row className="mb-4">
      <Col xs={12}>
        <div className="d-flex justify-content-end align-items-center gap-1 mb-2">
          {selectedVehicle && (
            <Button
              color="outline-danger"
              size="sm"
              onClick={() => {
                setSelectedVehicle(null)
                resetVehicleSpecs(DEFAULT_SPECS)
                if (onVehicleSpecsChange) {
                  onVehicleSpecsChange(DEFAULT_SPECS)
                }
              }}
              type="button"
            >
              <i className="bx bx-x me-1"></i>
              Clear
            </Button>
          )}

          <Button
            color="outline-primary"
            size="sm"
            onClick={() => {
              setShowVehicleSuggestions(!showVehicleSuggestions)
              if (!showVehicleSuggestions) {
                setSearchTerm("")
              }
            }}
            type="button"
          >
            <i className="bx bx-search me-1"></i>
            Search Vehicle
          </Button>
        </div>

        {showVehicleSuggestions && (
          <Card className="mb-3">
            <CardBody className="p-2">
              <div className="d-flex justify-content-end align-items-center mb-2">
                <Button
                  color="link"
                  size="sm"
                  className="p-0"
                  onClick={handleCloseSuggestions}
                >
                  <i className="bx bx-x"></i>
                </Button>
              </div>

              <div className="mb-2">
                <Input
                  type="text"
                  placeholder="Type vehicle name, chassis no, stock id, or engine no..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="form-control-sm"
                />
                <div className="d-flex justify-content-between align-items-center mt-1">
                  <small className="text-muted">
                    {searchTerm?.length === 0 && searchTerm?.length < 2
                      ? "Type at least 2 characters..."
                      : searchLoading || existVehicleLoading
                      ? ""
                      : `${searchResults?.length ?? 0} vehicles found`}
                  </small>

                  {searchTerm?.length >= 2 && (
                    <Button
                      color="primary"
                      size="sm"
                      onClick={handleManualSearch}
                      disabled={searchLoading || existVehicleLoading}
                      className="ms-2"
                    >
                      {searchLoading || existVehicleLoading ? (
                        <Spinner size="sm" />
                      ) : (
                        "Search"
                      )}
                    </Button>
                  )}
                </div>
              </div>

              {(searchLoading || existVehicleLoading) && (
                <div className="text-center p-3">
                  <Spinner size="sm" className="me-2" />
                  <small>Searching vehicles...</small>
                </div>
              )}

              {searchError && (
                <div className="text-center p-2 text-danger">
                  <small>{searchError}</small>
                </div>
              )}

              {!searchLoading && !existVehicleLoading && searchResults?.length > 0 && (
                <div className="search-results">
                  {searchResults?.map(vehicle => (
                    <div
                      key={vehicle.id}
                      className="p-2 border-bottom cursor-pointer hover-bg"
                      onClick={() => handleVehicleSelect(vehicle)}
                    >
                      <span className="fw-bold text-primary">
                        {[vehicle?.brand?.name, vehicle?.model?.name, vehicle?.year?.name, vehicle?.engine_c_c?.name ? `${vehicle?.engine_c_c?.name}CC` : null, vehicle?.fuel_type?.name, vehicle?.exterior_color?.name ? `${vehicle?.exterior_color?.name}Color` : null].filter(Boolean).join(" ") || "Unknown Vehicle"}
                      </span>
                      <span className="text-muted small ms-2">
                        | Stock: {vehicle?.stock_id || "-"} | Chassis: {vehicle?.full_chassis || "-"} | Engine: {vehicle?.full_engine || "-"} | Color: {vehicle?.exterior_color?.name || "-"} | Model: {vehicle?.year?.name || "-"} | CC: {vehicle?.engine_c_c?.name || "-"}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {!searchLoading &&
                !existVehicleLoading &&
                searchTerm?.length >= 2 &&
                searchResults.length === 0 && (
                  <div className="text-center p-2 text-muted">
                    <small>
                      No vehicles found. Try different search terms.
                    </small>
                  </div>
                )}

              {!searchLoading &&
                !existVehicleLoading &&
                searchTerm?.length === 0 &&
                searchResults?.length === 0 && (
                  <div className="text-center p-2 text-muted">
                    <small>Start typing to search for vehicles...</small>
                  </div>
                )}
            </CardBody>
          </Card>
        )}

        <Card className="border position-relative">
          {existVehicleLoading && (
            <div
              className="position-absolute w-100 h-100 d-flex flex-column align-items-center justify-content-center bg-white bg-opacity-75"
              style={{ zIndex: 5, minHeight: 120 }}
            >
              <Spinner size="sm" className="me-2" />
              <small className="text-muted">Loading vehicle...</small>
            </div>
          )}
          <CardBody className="p-3">
            <DraggableList
              items={vehicleSpecs}
              onDragEnd={handleSpecsDragEnd}
              onAdd={() => handleAddVehicleSpec({ key: "", value: "" })}
              onRemove={handleRemoveVehicleSpec}
              addButtonText="Add Specification"
              renderItem={renderVehicleSpec}
              minItems={1}
              compact
            />
          </CardBody>
        </Card>
      </Col>
    </Row>
  )
}

export default VehicleSpecification